import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';

const MAIN_HEADER_BG = '#1e3a8a';
const MAIN_HEADER_FG = '#ffffff';
const BORDER_LIGHT = '#e2e8f0';
const CELL_BORDER = `1px solid ${BORDER_LIGHT}`;
const ROW_STRIPE_A = '#ffffff';
const ROW_STRIPE_B = '#f8fafc';
const TEXT_PRIMARY = '#0f172a';
const METRIC_BLUE = '#1e40af';

const headerCellBase = {
  backgroundColor: MAIN_HEADER_BG,
  color: MAIN_HEADER_FG,
  fontWeight: 700,
  border: CELL_BORDER,
  padding: '10px 12px',
};

const DEFAULT_OPEN_REQ_COLUMNS = [
  { key: 'department', label: 'Department' },
  { key: 'city', label: 'City' },
  { key: 'ec', label: 'EC' },
  { key: 'role', label: 'Role' },
  { key: 'recruiter', label: 'Recruiter' },
  { key: 'createdAt', label: 'Created at' },
];

function formatDate(iso) {
  if (!iso) return '—';
  const s = String(iso);
  return s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
}

function normalizeOpenReqColumn(col) {
  if (!col || typeof col !== 'object') return col;
  const key = String(col.key || '').trim();
  const label = String(col.label || '').trim();
  if (key === 'function' || label.toLowerCase() === 'function') {
    return { ...col, key: 'department', label: 'Department' };
  }
  return col;
}

function formatCellValue(row, colKey) {
  if (!row || !colKey) return '—';
  const key = colKey === 'department' ? 'department' : colKey;
  const v =
    key === 'department'
      ? row.department ?? row.function
      : row[key];
  if (v === undefined || v === null || String(v).trim() === '') return '—';
  const k = String(colKey);
  if (k === 'createdAt' || k === 'updatedAt' || /At$/i.test(k)) return formatDate(v);
  return v;
}

function HrRacksheetOpenReq(props) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterOpenRequisitionsSummary();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load open requisitions',
          'error'
        );
        setData(null);
        return;
      }
      setData(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading open requisitions:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading open requisitions',
        'error'
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byDepartment = Array.isArray(data?.byDepartment) ? data.byDepartment : [];
  const openReqRows = Array.isArray(data?.openReqRows) ? data.openReqRows : [];

  const openReqColumns = useMemo(() => {
    const fromApi = data?.openRequisitionsTableColumns;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return fromApi.map(normalizeOpenReqColumn);
    }
    return DEFAULT_OPEN_REQ_COLUMNS;
  }, [data]);

  const colMinWidth = (key) => {
    switch (key) {
      case 'department':
      case 'function':
        return 110;
      case 'city':
        return 100;
      case 'ec':
        return 130;
      case 'role':
        return 130;
      case 'recruiter':
        return 110;
      case 'createdAt':
      case 'updatedAt':
        return 118;
      default:
        return 100;
    }
  };

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={2}
      >
        <Box>
          <Typography variant="h5" style={{ fontWeight: 700, color: MAIN_HEADER_BG }}>
            Open Requisitions — All Departments
          </Typography>
        </Box>
        <Button
          variant="outlined"
          disabled={loading}
          onClick={() => load()}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" style={{ gap: 32 }} mb={3}>
        <Box>
          <Typography variant="body2" color="textSecondary">
            Total Open Reqs
          </Typography>
          <Typography variant="h4" style={{ fontWeight: 700, color: METRIC_BLUE }}>
            {data?.totalOpenReqs ?? '—'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary">
            Buffer (IDD) — future
          </Typography>
          <Typography variant="h4" style={{ fontWeight: 700, color: METRIC_BLUE }}>
            {data?.bufferIddFuture ?? '—'}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap" style={{ gap: 24 }} alignItems="flex-start" mb={3}>

        <Box flex={1} minWidth={280}>
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8, color: TEXT_PRIMARY }}>
            By Department
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            style={{ border: CELL_BORDER }}
          >
            <Table size="small" style={{ borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ ...headerCellBase, textAlign: 'center', minWidth: 160 }}>Department</TableCell>
                  <TableCell style={{ ...headerCellBase, minWidth: 72, textAlign: 'center' }}>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byDepartment.map((row, idx) => {
                  const stripe = idx % 2 === 0 ? ROW_STRIPE_A : ROW_STRIPE_B;
                  return (
                    <TableRow key={`${row.departmentId || 'null'}-${row.department}-${idx}`}>
                      <TableCell align="center" style={{ border: CELL_BORDER, backgroundColor: stripe }}>{row.department || '—'}</TableCell>
                      <TableCell align="center" style={{ border: CELL_BORDER, backgroundColor: stripe }}>
                        {row.count ?? 0}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Box mb={4}>
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 10, color: TEXT_PRIMARY }}>
          Open Reqs (snapshot — filter Roster for live view)
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          style={{ border: CELL_BORDER, borderRadius: 0, overflowX: 'auto' }}
        >
          <Table size="small" style={{ borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  style={{ ...headerCellBase, minWidth: 60, textAlign: 'center' }}
                >
                  Sl. No.
                </TableCell>
                {openReqColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    align="center"
                    style={{
                      ...headerCellBase,
                      minWidth: colMinWidth(col.key),
                      textAlign: 'center',
                    }}
                  >
                    {col.label || col.key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {openReqRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(openReqColumns.length, 1) + 1}
                    align="center"
                    style={{ border: CELL_BORDER, padding: 16, color: '#64748b' }}
                  >
                    No rows
                  </TableCell>
                </TableRow>
              ) : (
                openReqRows.map((row, idx) => {
                  const stripe = idx % 2 === 0 ? ROW_STRIPE_A : ROW_STRIPE_B;
                  return (
                    <TableRow key={row._id || idx}>
                      <TableCell
                        align="center"
                        style={{ border: CELL_BORDER, backgroundColor: stripe }}
                      >
                        {idx + 1}
                      </TableCell>
                      {openReqColumns.map((col) => (
                        <TableCell
                          key={col.key}
                          align="center"
                          style={{ border: CELL_BORDER, backgroundColor: stripe }}
                        >
                          {formatCellValue(row, col.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetOpenReq);
